import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { cropAndCompress, formatFileSize, loadImage } from "../../utils/imageCompression";
import styles from "./ImageCropModal.module.css";

/**
 * Crop a picked menu photo to the card's shape, then shrink it to an
 * upload-friendly JPEG. Calls onDone with the new File.
 */
export default function ImageCropModal({ file, aspect = 4 / 3, onCancel, onDone }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    // Check it decodes before showing the cropper (iPhone HEIC photos don't outside Safari).
    loadImage(url)
      .then(() => setSrc(url))
      .catch(() => setError("This photo's format isn't supported. Please choose a JPG or PNG."));
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleDone = async () => {
    if (!src || !area) return;
    setSaving(true);
    setError(null);
    try {
      onDone(await cropAndCompress(src, area, file.name));
    } catch {
      setError("Couldn't process this photo. Please try another one.");
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} role="presentation" onClick={onCancel}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="crop-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="crop-title">Crop Photo</h3>
        <p className={styles.hint}>
          Drag to position and use the slider to zoom. Original: {formatFileSize(file.size)} — it's shrunk for upload.
        </p>

        <div className={styles.cropArea}>
          {src ? (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setArea(pixels)}
            />
          ) : (
            !error && <p className={styles.loading}>Loading photo…</p>
          )}
        </div>

        <label className={styles.zoom}>
          Zoom
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            disabled={!src}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </label>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>Cancel</button>
          <button type="button" onClick={handleDone} disabled={!src || !area || saving} className={styles.submitBtn}>
            {saving ? "Preparing…" : "Use Photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
